// use-hl-funding-history — 30s-Poll von /info `fundingHistory` für Plot.
// Default 48h Window. Reflows bei Coin-Wechsel.

import { useEffect, useMemo, useState } from 'react';
import {
  fetchFundingHistory,
  type FundingHistoryEntry,
} from '@/lib/hl-info-api';

const POLL_INTERVAL_MS = 30_000;
const DEFAULT_HOURS = 48;

export interface UseHlFundingHistoryOptions {
  enabled?: boolean;
  pollIntervalMs?: number;
  hoursBack?: number;
}

export interface UseHlFundingHistoryReturn {
  entries: FundingHistoryEntry[];
  loading: boolean;
  /** Durchschnittliche Funding-Rate über das Window (Decimal). */
  averageFunding: number | null;
  /** Anzahl positiver vs. negativer Stunden. */
  positiveCount: number;
  totalCount: number;
}

export function useHlFundingHistory(
  coin: string,
  options: UseHlFundingHistoryOptions = {},
): UseHlFundingHistoryReturn {
  const {
    enabled = true,
    pollIntervalMs = POLL_INTERVAL_MS,
    hoursBack = DEFAULT_HOURS,
  } = options;
  const [entries, setEntries] = useState<FundingHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !coin) {
      setEntries([]);
      return undefined;
    }
    let cancelled = false;

    const poll = async (): Promise<void> => {
      if (cancelled) return;
      setLoading(true);
      try {
        const next = await fetchFundingHistory(coin, hoursBack);
        if (!cancelled) setEntries(next);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void poll();
    const id = setInterval(() => void poll(), pollIntervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled, coin, pollIntervalMs, hoursBack]);

  return useMemo(() => {
    if (entries.length === 0) {
      return {
        entries,
        loading,
        averageFunding: null,
        positiveCount: 0,
        totalCount: 0,
      };
    }
    const sum = entries.reduce((s, e) => s + e.fundingRate, 0);
    const positiveCount = entries.filter((e) => e.fundingRate > 0).length;
    return {
      entries,
      loading,
      averageFunding: sum / entries.length,
      positiveCount,
      totalCount: entries.length,
    };
  }, [entries, loading]);
}
